import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandHoldingHeart,
  faUsers,
  faDog,
} from "@fortawesome/free-solid-svg-icons";

const About: React.FC = () => {
  const values = [
    {
      icon: faHandHoldingHeart,
      title: "Commitment",
      description:
        "We empower fosters and adopters with educational resources to navigate challenges and maintain a safe, stable home for their dogs.",
    },
    {
      icon: faUsers,
      title: "Community",
      description:
        "We connect dog lovers who want to make a difference with frontline rescuers to save lives together.",
    },
    {
      icon: faDog,
      title: "Second Chances",
      description:
        "We support shelter and rescue dogs on their journey to the safe, loving fresh start they deserve.",
    },
  ];

  const teamMembers = [
    {
      role: "Founder",
      name: "Nikki Leonard",
      image:
        "https://pup-profile-prod.s3.us-west-2.amazonaws.com/about/Nikki.jpg",
      paragraphs: [
        "Nikki is a healthcare industry professional, part-time Navy pilot, and devoted dog rescue volunteer. Following the loss of her senior rescue dog in 2020, she began fostering with The Cantu Foundation in San Diego, deepening her commitment to animal rescue.",
        "Nikki earned her MBA from UC San Diego, during which time she decided to channel her resources into addressing the animal welfare crisis in Southern CA shelters. A talented team of like-minded dog lovers partnered with her to bring the nonprofit venture, now known as Pup Profile, to life.",
      ],
    },
    {
      role: "Vice President",
      name: "Jessica Grupp",
      image:
        "https://pup-profile-prod.s3.us-west-2.amazonaws.com/about/jessica-new.jpg",
      paragraphs: [
        "Part-time Navy pilot, full-time tech product manager, and most importantly, Kanna's devoted dog mom. Kanna (like Donna, but with a K) is a social butterfly who loves everyone, always ready with a lick, and firmly believes that ball is life.",
        "With an MBA from the University of Michigan and 8 years of experience volunteering at dog, horse, and cat rescues, Jessica has a passion for helping animals find their forever homes and giving them the second chance they deserve.",
      ],
    },
  ];

  return (
    <section className="font-poppins text-gray-dark">
      <section className="flex w-full flex-col items-center justify-center px-6 py-16 lg:px-16 lg:py-28">
        <div className="flex w-full max-w-[900px] flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <span className="w-fit rounded bg-blue-pale px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
              About pup profile
            </span>
            <h1 className="text-center text-4xl font-extrabold text-black">
              Who we are
            </h1>
          </div>
          <p className="text-center text-xl font-normal text-gray-dark">
            Pup Profile is a volunteer-run and donation-based 501(c)(3)
            nonprofit organization founded in 2024. Our team includes a diverse
            group of professionals, military veterans, MBAs, dog rescuers and
            entrepreneurs who all share a love for dogs.
          </p>
        </div>
      </section>

      {/* Our values */}
      <section className="flex w-full flex-col items-center gap-12 bg-blue-pale px-6 py-20 lg:px-16">
        <h2 className="text-center text-3xl font-bold text-blue-strong">
          Our values
        </h2>
        <div className="flex flex-wrap items-start justify-center gap-10">
          {values.map((value) => (
            <div
              key={value.title}
              className="flex max-w-[350px] flex-col items-center gap-8 p-6"
            >
              <FontAwesomeIcon
                icon={value.icon}
                className="text-[2.5rem] text-blue"
              />
              <div className="flex flex-col items-center gap-3">
                <h3 className="text-2xl font-semibold text-black">
                  {value.title}
                </h3>
                <p className="text-center text-base font-normal text-gray-dark">
                  {value.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why we exist */}
      <section className="flex w-full flex-col items-center justify-between gap-10 px-6 py-12 lg:flex-row lg:gap-16 lg:px-16 lg:py-16">
        <div className="flex w-full flex-col gap-6 lg:flex-1">
          <div className="flex flex-col gap-3">
            <span className="w-fit rounded bg-blue-pale px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
              Why we exist
            </span>
            <h2 className="text-3xl font-bold text-blue-strong">
              Every dog deserves a chance
            </h2>
          </div>
          <div className="flex flex-col gap-4 text-base font-normal text-black">
            <p>
              Shelters are experiencing a critical state of overcrowding,
              resulting in adoptable dogs being euthanized at an alarming rate.
              California shelters collectively euthanized over 41,000 dogs in
              2025.*
            </p>
            <p>
              Seeing rescuers work tirelessly to try to save these dogs within a
              chaotic social media environment only to be met by empty donation
              pledges and unreliable foster offers motivated our founder to try
              to approach the problem differently.
            </p>
            <p className="text-sm text-gray-dark">
              *{" "}
              <span className="italic">
                Source: Shelter Animals Count Data Dashboard
              </span>
            </p>
          </div>
        </div>
        <div className="w-full lg:flex-1">
          <img
            src="https://pup-profile-prod.s3.us-west-2.amazonaws.com/about/why-we-exist.jpg"
            alt="A happy rescue dog looking up in the grass"
            className="aspect-square w-full rounded-xl object-cover"
          />
        </div>
      </section>

      {/* Meet the team */}
      <section className="flex w-full flex-col items-center gap-12 bg-blue-pale py-20">
        <h2 className="text-center text-3xl font-bold text-blue-strong">
          Meet the team
        </h2>
        <div className="flex w-full flex-col items-stretch justify-center gap-12 px-6 lg:flex-row lg:gap-0 lg:px-16">
          {teamMembers.map((member) => (
            <div
              key={member.name}
              className="flex flex-col items-center lg:min-w-0 lg:flex-1 lg:px-16"
            >
              <div className="relative aspect-[504/302] w-full overflow-hidden border-l-[12px] border-solid border-orange">
                <img
                  src={member.image}
                  alt={member.name}
                  className="absolute inset-0 size-full object-cover"
                />
              </div>
              <div className="flex w-full flex-col gap-6 py-8">
                <div className="flex flex-col gap-3">
                  <span className="text-sm font-medium uppercase tracking-[0.42px] text-gray-dark">
                    {member.role}
                  </span>
                  <h3 className="text-3xl font-bold text-blue-strong">
                    {member.name}
                  </h3>
                </div>
                <div className="flex flex-col gap-4 text-base font-normal text-black">
                  {member.paragraphs.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </section>
  );
};

export default About;
